import React from 'react';
import { Conta, Banco, TipoContaId } from '../../types/financeiro';
import { DollarSign, CreditCard, Landmark, AlertCircle, ArrowUpRight, ArrowDownRight, CheckCircle2 } from 'lucide-react';
import { MaskedValue } from '../common/ProtectedField';

interface FinanceiroResumoCardsProps {
  contas: Conta[];
  bancosCount: number;
  activeTab: string;
}

export function FinanceiroResumoCards({ contas, bancosCount, activeTab }: FinanceiroResumoCardsProps) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const entradas = contas.filter(c => Number(c.tipo_conta_id) === TipoContaId.RECEBER);
  const saidas = contas.filter(c => Number(c.tipo_conta_id) === TipoContaId.PAGAR);

  const totalReceberPendente = entradas
    .filter(c => !c.dt_pagamento)
    .reduce((acc, c) => acc + (c.valor || 0), 0);

  const totalRecebido = entradas
    .filter(c => Boolean(c.dt_pagamento))
    .reduce((acc, c) => acc + (c.valor_pago || c.valor || 0), 0);

  const totalPagarPendente = saidas
    .filter(c => !c.dt_pagamento)
    .reduce((acc, c) => acc + (c.valor || 0), 0);

  const totalPago = saidas
    .filter(c => Boolean(c.dt_pagamento))
    .reduce((acc, c) => acc + (c.valor_pago || c.valor || 0), 0);

  const atrasadasCount = contas.filter(c => {
    if (c.dt_pagamento || !c.dt_vencimento) return false;
    const v = new Date(c.dt_vencimento + 'T00:00:00');
    return v < hoje;
  }).length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      
      {/* Card 1: A Receber */}
      <div className="bg-white p-4.5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-emerald-300 transition-colors">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            A Receber (Pendente)
          </span>
          <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
            <ArrowUpRight className="w-5 h-5" />
          </div>
        </div>
        <div className="mt-3">
          <div className="text-2xl font-extrabold text-emerald-600">
            <MaskedValue
              permission="view_financial_values"
              value={totalReceberPendente}
              type="currency"
            />
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Já recebido:{' '}
            <span className="font-semibold text-slate-700">
              <MaskedValue
                permission="view_financial_values"
                value={totalRecebido}
                type="currency"
              />
            </span>
          </p>
        </div>
      </div>

      {/* Card 2: A Pagar */}
      <div className="bg-white p-4.5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-rose-300 transition-colors">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            A Pagar (Pendente)
          </span>
          <div className="p-2 bg-rose-50 text-rose-600 rounded-xl">
            <ArrowDownRight className="w-5 h-5" />
          </div>
        </div>
        <div className="mt-3">
          <div className="text-2xl font-extrabold text-rose-600">
            <MaskedValue
              permission="view_financial_values"
              value={totalPagarPendente}
              type="currency"
            />
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Já quitado:{' '}
            <span className="font-semibold text-slate-700">
              <MaskedValue
                permission="view_financial_values"
                value={totalPago}
                type="currency"
              />
            </span>
          </p>
        </div>
      </div>

      {/* Card 3: Contas Atrasadas */}
      <div className={`p-4.5 rounded-2xl border shadow-sm flex flex-col justify-between transition-colors ${
        atrasadasCount > 0 ? 'bg-amber-50/50 border-amber-300' : 'bg-white border-slate-200'
      }`}>
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Contas Vencidas
          </span>
          <div className={`p-2 rounded-xl ${atrasadasCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
            <AlertCircle className="w-5 h-5" />
          </div>
        </div>
        <div className="mt-3">
          <p className={`text-2xl font-extrabold ${atrasadasCount > 0 ? 'text-amber-700' : 'text-slate-800'}`}>
            {atrasadasCount} <span className="text-sm font-semibold text-slate-500">títulos</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {atrasadasCount > 0 ? 'Requer atenção imediata' : 'Nenhuma conta vencida'}
          </p>
        </div>
      </div>

      {/* Card 4: Bancos & Total Lançamentos */}
      <div className="bg-white p-4.5 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between hover:border-blue-300 transition-colors">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Lançamentos Totais
          </span>
          <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
            <CreditCard className="w-5 h-5" />
          </div>
        </div>
        <div className="mt-3">
          <p className="text-2xl font-extrabold text-slate-900">
            {contas.length} <span className="text-sm font-semibold text-slate-500">registros</span>
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {bancosCount} banco(s) cadastrado(s)
          </p>
        </div>
      </div>

    </div>
  );
}
